import type { ActionItem as StitchActionItem } from "@elation/api-client/src/stitch-web";
import {
  useGetEncounterById,
  useListActionItemsForEncounter,
  useListAgendaItems,
} from "@elation/api-client/src/stitch-web";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Spinner } from "@src/components/ui/spinner";
import type { ComponentProps } from "react";
import { useMemo } from "react";
import styles from "./ActionItems.module.less";
import {
  type ActionItemSection,
  categoryToSectionHeader,
} from "./ActionItems.types";
import { ActionItemsSection } from "./ActionItemsSection";

type ActionItemsSectionActionItems = ComponentProps<
  typeof ActionItemsSection
>["actionItems"];

export interface ActionItemsProps {
  encounterId: number;
}

export const ActionItems = ({ encounterId }: ActionItemsProps) => {
  const enabled = Number.isFinite(encounterId) && encounterId > 0;

  const { isLoading: isLoadingEncounter } = useGetEncounterById(encounterId, {
    query: { enabled },
  });

  const { isLoading: isLoadingAgendaItems } = useListAgendaItems(encounterId, {
    query: { enabled },
  });

  const { data: actionItemsResponse, isLoading: isLoadingActionItems } =
    useListActionItemsForEncounter(encounterId, {
      query: { enabled },
    });

  const actionItemsByCategory = useMemo((): Record<
    ActionItemSection,
    ActionItemsSectionActionItems
  > => {
    const rows = actionItemsResponse?.data.items ?? [];

    return rows.reduce(
      (
        acc: Record<ActionItemSection, ActionItemsSectionActionItems>,
        actionItem: StitchActionItem,
      ) => {
        const section =
          categoryToSectionHeader[
            actionItem.category as keyof typeof categoryToSectionHeader
          ] ?? "Miscellaneous";

        if (!acc[section]) {
          acc[section] = [];
        }

        acc[section].push({
          ...actionItem,
          agendaItem: null,
        });

        return acc;
      },
      {
        Prescriptions: [],
        Orders: [],
        Referrals: [],
        Miscellaneous: [],
        Assessments: [],
        "Patient Education": [],
      },
    );
  }, [actionItemsResponse?.data.items]);

  const isLoading =
    isLoadingEncounter ||
    (enabled && isLoadingAgendaItems) ||
    (enabled && isLoadingActionItems);

  if (!enabled) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">Invalid encounter.</Text>
      </HorizontalFlexbox>
    );
  }

  if (isLoading) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3"
      >
        <Spinner />
      </HorizontalFlexbox>
    );
  }

  const totalCount = Object.values(actionItemsByCategory).reduce(
    (n, items) => n + items.length,
    0,
  );

  if (totalCount === 0) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">
          No action items from completed agenda items
        </Text>
      </HorizontalFlexbox>
    );
  }

  return (
    <VerticalFlexbox
      className={`${styles.root} lg:min-h-0 lg:flex-1 lg:overflow-auto`}
    >
      <VerticalFlexbox as="ul" className={styles.actionItems}>
        {Object.entries(actionItemsByCategory).map(([category, items]) =>
          items.length > 0 ? (
            <ActionItemsSection
              key={category}
              section={category as ActionItemSection}
              actionItems={items}
            />
          ) : null,
        )}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
};
